import { createClusterPopup, createPointPopup } from './popups';

/**
 * Normalize territory country codes to parent country
 * (e.g. PR -> US)
 */
const normalizeCountryCode = (code) => {
    if (!code) return code;

    const upper = code.toUpperCase();

    const TERRITORY_MAP = {
        PR: 'US',
    };

    return (TERRITORY_MAP[upper] ?? upper).toLowerCase();
};

export const setupMapEventHandlers = (
    map,
    {
        setLng,
        setLat,
        setZoom,
        setActiveCountryCode,
        setActiveHolding,
        setActiveCluster,
        handleCountryClick,
        isSingleCountry,
        isPointPopupShown,
        countryList,
        popupRef,
    }
) => {
    map.on('move', () => {
        setLng(map.getCenter().lng.toFixed(4));
        setLat(map.getCenter().lat.toFixed(4));
        setZoom(map.getZoom().toFixed(2));
    });

    map.on('click', 'clusters', (e) =>
        handleClusterClick(e, map, {
            setActiveCluster,
            setActiveHolding,
            setActiveCountryCode,
            handleCountryClick,
            isSingleCountry,
            isPointPopupShown,
            countryList,
        })
    );

    map.on('click', 'unclustered-point', (e) =>
        handlePointClick(e, map, {
            setActiveHolding,
            setActiveCluster,
            setActiveCountryCode,
            handleCountryClick,
            isSingleCountry,
            isPointPopupShown,
            countryList,
            popupRef,
        })
    );

    map.on('mouseenter', 'clusters', () => {
        map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'clusters', () => {
        map.getCanvas().style.cursor = '';
    });
    map.on('mouseenter', 'unclustered-point', () => {
        map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'unclustered-point', () => {
        map.getCanvas().style.cursor = '';
    });
};

export const handleClusterClick = (
    e,
    map,
    {
        setActiveCluster,
        setActiveHolding,
        setActiveCountryCode,
        handleCountryClick,
        isSingleCountry,
        isPointPopupShown,
        countryList,
    }
) => {
    const features = map.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
    });

    if (features.length === 0) return;

    const cluster = features[0];
    const clusterId = cluster.properties.cluster_id;
    const currentZoom = map.getZoom();

    const source = map.getSource('points');

    source.getClusterLeaves(clusterId, Infinity, 0, (err, leaves) => {
        if (err) return;

        const params = new URLSearchParams(window.location.search);
        params.delete('id');
        window.history.replaceState(
            {},
            '',
            `${window.location.pathname}?${params.toString()}`
        );

        setActiveHolding(null);

        const allProperties = leaves.map((leaf) => leaf.properties);
        const coordinates = cluster.geometry.coordinates;

        const [lng, lat] = leaves[0].geometry.coordinates;
        const isStacked = leaves.every(
            (leaf) =>
                leaf.geometry.coordinates[0] === lng &&
                leaf.geometry.coordinates[1] === lat
        );

        const showCarousel = () => {
            setActiveCluster(allProperties);
            map.flyTo({ center: coordinates, duration: 1000 });

            if (!isPointPopupShown) return;

            const showAddressMissing = allProperties.some((property) =>
                ['city', 'country'].includes(property.coordinateLevel)
            );

            createClusterPopup(
                map,
                coordinates,
                allProperties,
                showAddressMissing
            );
        };

        if (isStacked) {
            showCarousel();
            return;
        }

        source.getClusterExpansionZoom(clusterId, (zoomErr, zoom) => {
            if (zoomErr) return;

            if (zoom > map.getMaxZoom()) {
                showCarousel();
                return;
            }

            map.flyTo({ center: coordinates, zoom: zoom + 1.4 });
        });
    });

    const boundaries = map.queryRenderedFeatures(e.point, {
        layers: ['country-boundaries'],
    });

    if (boundaries.length > 0) {
        const rawCountry = boundaries[0].properties.iso_3166_1;
        const country = normalizeCountryCode(rawCountry);

        setActiveCountryCode(country);

        if (currentZoom < 1.99 && !isSingleCountry) {
            handleCountryClick({
                code: country,
                name: countryList.countries.find(
                    (c) => c.code.toUpperCase() === country.toUpperCase()
                )?.name,
            });
        }
    }
};

export const handlePointClick = (
    e,
    map,
    {
        setActiveHolding,
        setActiveCluster,
        setActiveCountryCode,
        handleCountryClick,
        isSingleCountry,
        isPointPopupShown,
        countryList,
        popupRef,
    }
) => {
    const properties = e.features[0].properties;

    // Use source data coordinates instead of rendered feature coordinates
    // to avoid floating-point drift from Mapbox's tile projection
    const sourceData = map.getSource('points')?._data;
    const sourceFeature = sourceData?.features?.find(
        (f) => f.properties.sourceId === properties.sourceId
    );
    const coordinates = sourceFeature
        ? sourceFeature.geometry.coordinates.slice()
        : e.features[0].geometry.coordinates.slice();

    setActiveHolding(properties.sourceId);
    setActiveCluster(null);

    if (popupRef?.current) {
        popupRef.current.remove();
        popupRef.current = null;
    }

    let popup = null;
    if (isPointPopupShown) {
        popup = createPointPopup(map, coordinates, properties);
        if (popupRef) popupRef.current = popup;
    }

    const features = map.queryRenderedFeatures(e.point, {
        layers: ['country-boundaries'],
    });

    if (features.length > 0) {
        const currentZoom = map.getZoom();
        const rawCountry = features[0].properties.iso_3166_1;
        const country = normalizeCountryCode(rawCountry);

        setActiveCountryCode(country);

        if (currentZoom < 3.8 && !isSingleCountry) {
            handleCountryClick({
                code: country,
                name: countryList.countries.find(
                    (c) => c.code.toUpperCase() === country.toUpperCase()
                )?.name,
            });
        }
    }

    const params = new URLSearchParams(window.location.search);
    params.set('id', properties.sourceId);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
};
